import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from "video.js";
import "videojs-contrib-quality-levels";
import "videojs-hotkeys";
import "videojs-http-source-selector";
import "videojs-seek-buttons";
import styles from "./UgcPlayer.module.scss";
import { UGCList } from "../../../../../../models/home-rows";
import { detectQualityLable } from "../../../../../../services/player-service";
import {
  PlayerAction,
  PlayerState
} from "../../../../../../components/app-video/video-models";

interface VideoJsProps {
  ugcList: UGCList;
  isPlayerPage: boolean;
  options: VideoJsPlayerOptions;
  customControls: JSX.Element;
  action: PlayerAction;
  state: PlayerState;
  onState: Dispatch<SetStateAction<PlayerState>>;
  onEndHandler: () => Promise<void>;
}

function UgcPlayer({
  ugcList,
  isPlayerPage,
  options,
  customControls,
  action,
  state,
  onState,
  onEndHandler
}: VideoJsProps) {
  const videoRef = useRef(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);

  const [isPaused, setIsPaused] = useState<boolean>(true);
  const [isVideoEnded, setIsVideoEnded] = useState<boolean>(false);
  const timerVideoEndedRef = useRef<NodeJS.Timeout | null>(null);

  const changeQualityLabel = () => {
    const player = playerRef.current;
    player &&
      player.controlBar
        .el()
        .querySelectorAll(
          ".vjs-control-bar .vjs-menu-content .vjs-menu-item .vjs-menu-item-text"
        )
        .forEach(element => {
          let qualityList = [160, 240, 480, 720, 1080];

          if (/\d/.test(element.innerHTML)) {
            let bitrateInNumber = Number(
              element.innerHTML.match(/\d/g)?.join("")
            );
            if (!qualityList.includes(bitrateInNumber)) {
              element.innerHTML = detectQualityLable(bitrateInNumber);
            }
          } else if (element.innerHTML === "Auto") {
            element.innerHTML = detectQualityLable(0);
          }
        });
  };

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const defaults = {
        fullscreen: {
          enterOnRotate: true,
          exitOnRotate: true,
          alwaysInLandscapeMode: true,
          iOS: true
        }
      };

      const angle = () => {
        // iOS
        if (typeof window.orientation === "number") {
          return window.orientation;
        }
        // Android
        if (screen && screen.orientation && screen.orientation.angle) {
          return window.orientation;
        }
        videojs.log("angle unknown");
        return 0;
      };

      const screen = window.screen;
      //@ts-ignore
      const lockOrientationUniversal = (mode: OrientationLockType) => {
        screen.orientation &&
          //@ts-ignore
          screen.orientation.lock(mode).then(
            () => {},
            err => console.log(err)
          );
      };

      const player = (playerRef.current = videojs(videoElement, options, () => {
        videojs.log("player is ready");

        player.addClass("vjs-landscape-fullscreen");

        const rotationHandler = () => {
          const currentAngle = angle();

          if (
            currentAngle === 90 ||
            currentAngle === 270 ||
            currentAngle === -90
          ) {
            if (
              defaults.fullscreen.enterOnRotate &&
              player.paused() === false
            ) {
              player.requestFullscreen();
              lockOrientationUniversal("landscape");
            }
          }
          if (currentAngle === 0 || currentAngle === 180) {
            if (defaults.fullscreen.exitOnRotate && player.isFullscreen()) {
              player.exitFullscreen();
            }
          }
        };

        if (videojs.browser.IS_IOS) {
          window.addEventListener("orientationchange", rotationHandler);
        } else if (screen && screen.orientation) {
          // addEventListener('orientationchange') is not a user interaction on Android
          screen.orientation.onchange = rotationHandler;
        }

        player.on("fullscreenchange", () => {
          // console.log("On-full-screen");

          if (videojs.browser.IS_ANDROID || videojs.browser.IS_IOS) {
            if (
              !angle() &&
              player.isFullscreen() &&
              defaults.fullscreen.alwaysInLandscapeMode
            ) {
              lockOrientationUniversal("landscape");
            }
          }
        });

        player.on("timeupdate", () => {
          if (player.duration() - player.currentTime() < 0.5) {
            return onState(s => ({
              ...s,
              currentVolumeLevel: safeVolumeNumber(player.volume()),
              currentTime: safePositiveNumber(player.currentTime()),
              isEnded: true,
              isLoading: false
            }));
          }
          onState(s => ({
            ...s,
            currentVolumeLevel: safeVolumeNumber(player.volume()),
            currentTime: safePositiveNumber(player.currentTime()),
            isEnded: false,
            isLoading: false
          }));
        });
        player.on("loadedmetadata", () =>
          onState(s => ({
            ...s,
            duration: safePositiveNumber(player.duration())
          }))
        );

        player.on("dispose", () => {
          window.removeEventListener("orientationchange", rotationHandler);

          let rangeOfTime = 0;
          for (let i = 0; i < player.played().length; i++) {
            let start = player.played().start(i);
            let end = player.played().end(i);
            rangeOfTime = Math.floor(rangeOfTime + (end - start));
          }
        });

        player.on("pause", () => {
          setIsPaused(true);
          onState(s => ({
            ...s,
            isPlaying: false,
            isEnded: false,
            isLoading: false
          }));
        });

        player.on("play", () => {
          setIsPaused(false);
          onState(s => ({
            ...s,
            isPlaying: true,
            isEnded: false,
            isLoading: false
          }));
        });

        player.on("loadeddata", () => {
          const promise = player.play();
          if (promise !== undefined) {
            promise
              .then(function () {
                // Autoplay started!
              })
              .catch(function (error) {
                player.muted(true);
                player.play();
              });
          }
        });

        player.on("ended", async () => {
          player.ended();
          await onEndHandler();

          //end of promotions
          if (!isPlayerPage) {
            setIsVideoEnded(true);
            onState(s => ({ ...s, isEnded: true, isLoading: false }));
          }
        });
      }));

      player.qualityLevels().on("change", () => {
        setTimeout(changeQualityLabel, 0);
      });

      options?.autoplay && player.autoplay(options?.autoplay);
      options?.sources && player.src() && player.src(options.sources);
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timerVideoEndedRef.current && clearTimeout(timerVideoEndedRef.current);
    };
  }, [isPlayerPage, options.sources, options.autoplay, videoRef]);

  useEffect(() => {
    if (!playerRef.current || !options.sources) return;

    const videoElement = videoRef.current;
    if (!videoElement) return;
    let interval: NodeJS.Timer;
    const player = playerRef.current;

    interval = setInterval(() => {
      //calculate watched_length
      let rangeOfTime = 0;
      for (let i = 0; i < player.played().length; i++) {
        let start = player.played().start(i);
        let end = player.played().end(i);
        rangeOfTime = Math.floor(rangeOfTime + (end - start));
      }
    }, 10000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef]);

  useEffect(() => {
    if (!playerRef.current || !options.sources || !action?.action) return;
    const currentPlayer = playerRef.current;

    switch (action.action) {
      case "play":
        currentPlayer.play();
        break;
      case "pause":
        currentPlayer.pause();
        break;
      case "end":
        currentPlayer.ended();
        break;
      case "skipTime":
        if (playerRef.current) playerRef.current.currentTime(action.value);
        break;
      case "volumeLevel":
        if (playerRef.current) {
          playerRef.current.volume(action.value);
          onState(s => ({
            ...s,
            currentVolumeLevel: safeVolumeNumber(action.value)
          }));
        }
        break;
      default:
        break;
    }
  }, [action, onState, state.isMuted]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player) {
        player?.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <>
      <div className={styles.video_wrapper}>
        <div
          data-vjs-player
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            borderRadius: "12px"
          }}
        >
          <video
            ref={videoRef}
            className={`video-js `}
            {...(!isPlayerPage && { "data-promotion-player": true })}
          />
          {customControls}
        </div>
      </div>
    </>
  );
}

export default UgcPlayer;

const safePositiveNumber = (duration: number) =>
  isNaN(duration) ? -1 : duration;

const safeVolumeNumber = (volume: number) => {
  if (isNaN(volume)) return 0;
  if (volume > 1) return 1;
  if (volume < 0) return 0;
  return volume;
};
